import React, { useEffect, useRef } from 'react';
import PostLoginLoader from '../../features/auth/components/PostLoginLoader';
import DivisionSwitchLoader from '../../features/organization/components/DivisionSwitchLoader';
import { DIVISION_SWITCH_STORAGE_KEY } from '../../features/organization/utils/switchDivisionSession';

// Suspense fallback for the top-level route tree in App.js. Normally this is just
// PostLoginLoader (the purple splash), shown while a lazy route chunk loads on a fresh page
// load. But switchDivisionSession() does a hard `window.location.href` reload to guarantee
// every component remounts under the new division/tokens — and during the split second where
// the reloaded page's JS is still booting, React has nothing mounted yet except this Suspense
// fallback, so PostLoginLoader would flash in right after DivisionSwitchLoader's own
// "Switching to X..." screen, reading as two different loaders back to back for one action.
// switchDivisionSession stashes the division name in sessionStorage right before reloading
// (sessionStorage — unlike React state — survives the reload); if it's there, show
// DivisionSwitchLoader instead so the whole transition reads as one continuous animation.
const AppSuspenseFallback = () => {
  // Read once per mount (Suspense can remount this fallback each time it re-suspends) —
  // captured in a ref so the flag's removal below doesn't flip the loader mid-render.
  const divisionNameRef = useRef(undefined);
  if (divisionNameRef.current === undefined) {
    try {
      divisionNameRef.current = sessionStorage.getItem(DIVISION_SWITCH_STORAGE_KEY);
    } catch (error) {
      // sessionStorage can throw in some privacy modes — fall back to the normal loader
      divisionNameRef.current = null;
    }
  }

  useEffect(() => {
    // One-shot flag — clear it now that it's been read, so a later unrelated Suspense
    // trigger in the same tab session (e.g. navigating to a different lazy route) doesn't
    // reuse it and show the division-switch loader for no reason.
    if (divisionNameRef.current) {
      try {
        sessionStorage.removeItem(DIVISION_SWITCH_STORAGE_KEY);
      } catch (error) {
        // Non-fatal.
      }
    }
  }, []);

  if (divisionNameRef.current) {
    return <DivisionSwitchLoader divisionName={divisionNameRef.current} activeStep={3} />;
  }

  return <PostLoginLoader />;
};

export default AppSuspenseFallback;
